import React, {Fragment, useContext} from 'react';
import glamorous from 'glamorous';
import PropTypes from 'prop-types';
import DescriptionCard from '../../../shared/library/cards/description';
import {makeSentence} from '../../../shared/utils/make-sentence';
import ItemList from '../../../shared/library/cards/item-list/';
import {compose, Query} from 'react-apollo';
import {companyStacksUsing, userStacksUsing} from '../../../data/tool-profile/queries';
import {ComponentContainer, StyledSmallTitle, Spinner, Center} from './shared/styles';
import Circular, {LARGE} from '../../../shared/library/indicators/indeterminate/circular';
import {updateQuery} from '../utils';
import NavAnchor, {NAV_ANCHOR_OFFSET} from '../../tool-profile/components/shared/nav-anchor';
import {flattenEdges} from '../../../shared/utils/graphql';
import {TARMAC} from '../../../shared/style/colors';
import {MobileContext} from '../../../shared/enhancers/mobile-enhancer';
import SignUp from '../../../shared/library/buttons/sign-up';
import {withAnalyticsPayload} from '../../../shared/enhancers/analytics-enhancer';
import Simple from '../../../shared/library/buttons/base/simple';
import {API_ROUTE} from '../../stacks/constants/routes';
import withErrorBoundary from '../../../shared/enhancers/error-boundary';

const COMPANY_STACKS_USING = 'company_stacks_using';
const USER_STACKS_USING = 'user_stacks_using';

const Container = glamorous.div({
  margin: '25px 0'
});

const Description = glamorous.div({
  color: TARMAC
});

const CompaniesUsingDescription = ({toolName, firstThreeCompanies, companyStacksUsingCount}) => {
  return (
    <Description>
      <strong>{companyStacksUsingCount}</strong> companies reportedly use{' '}
      <strong>{toolName}</strong> in their tech stacks, including{' '}
      {makeSentence(firstThreeCompanies, true)}.
    </Description>
  );
};

const Stacks = ({tool, currentUser}) => {
  const isMobile = useContext(MobileContext);
  return (
    <ComponentContainer>
      <NavAnchor id="stacks" offset={NAV_ANCHOR_OFFSET} />
      <Query
        query={companyStacksUsing}
        variables={{id: tool.id, first: isMobile ? 8 : 9}}
        notifyOnNetworkStatusChange={true}
      >
        {({loading, data, fetchMore}) => {
          const hasData = data && data.tool;
          if (loading && !hasData)
            return (
              <Spinner>
                <Circular size={LARGE} />
              </Spinner>
            );
          if (hasData) {
            const companyStacksUsing = flattenEdges(data.tool.companyStacksUsing);
            const apiSignup = companyStacksUsing.length === 45;
            const companyStacksUsingCount = data.tool.companyStacksUsing.count;
            const firstThreeCompanies = companyStacksUsing.slice(0, 3).map(company => {
              return company.name;
            });

            const companyData = companyStacksUsing.map(company => {
              return {
                name: company.name,
                imageUrl: company.imageUrl,
                thumbUrl: company.thumbRetinaUrl,
                slug: company.identifier,
                path: company.path,
                id: company.id
              };
            });

            const endCursor = data.tool.companyStacksUsing.pageInfo.endCursor;
            const hasNextPage = data.tool.companyStacksUsing.pageInfo.hasNextPage;

            const onCompanyLoadMoreClick = () => {
              fetchMore({
                variables: {
                  after: endCursor,
                  first: isMobile ? 16 : 18
                },
                updateQuery
              });
            };

            return (
              <Fragment>
                {companyStacksUsingCount !== 0 && (
                  <Fragment>
                    <DescriptionCard>
                      <StyledSmallTitle>Companies</StyledSmallTitle>
                      {companyStacksUsing.length >= 3 && (
                        <CompaniesUsingDescription
                          companyStacksUsingCount={companyStacksUsingCount}
                          firstThreeCompanies={firstThreeCompanies}
                          toolName={tool.name}
                        />
                      )}
                    </DescriptionCard>
                    <Container>
                      <ItemList
                        notFoundItem="company stacks"
                        item={companyData}
                        hasNextPage={currentUser && hasNextPage && !apiSignup}
                        loading={loading}
                        loadMore={onCompanyLoadMoreClick}
                        currentUser={currentUser}
                      />
                      <Center>
                        {!currentUser && hasNextPage && (
                          <SignUp text="Sign up to see more" section={COMPANY_STACKS_USING} />
                        )}
                        {apiSignup && (
                          <Simple onClick={() => (window.location.href = API_ROUTE)}>
                            Get more companies via the API
                          </Simple>
                        )}
                      </Center>
                    </Container>
                  </Fragment>
                )}
              </Fragment>
            );
          }
        }}
      </Query>
      <Query
        query={userStacksUsing}
        variables={{id: tool.id, first: isMobile ? 8 : 9}}
        notifyOnNetworkStatusChange={true}
      >
        {({loading, data, fetchMore}) => {
          const hasData = data && data.tool;
          if (loading && !hasData)
            return (
              <Spinner>
                <Circular size={LARGE} />
              </Spinner>
            );

          if (hasData) {
            const userStacksUsing = flattenEdges(data.tool.userStacksUsing);
            const userStacksUsingCount = data.tool.userStacksUsing.count;

            const developerData =
              userStacksUsing &&
              userStacksUsing.map(developer => {
                return {
                  name: developer.name,
                  imageUrl: developer.imageUrl,
                  thumbUrl: developer.thumbRetinaUrl,
                  slug: `${developer.identifier}`,
                  id: developer.id
                };
              });

            const endCursor = data.tool.userStacksUsing.pageInfo.endCursor;
            const hasNextPage = data.tool.userStacksUsing.pageInfo.hasNextPage;

            const onDeveloperLoadMoreClick = () => {
              fetchMore({
                variables: {
                  after: endCursor,
                  first: isMobile ? 16 : 18
                },
                updateQuery
              });
            };
            return (
              <Fragment>
                {userStacksUsingCount !== 0 && (
                  <Fragment>
                    <DescriptionCard>
                      <StyledSmallTitle>Developers</StyledSmallTitle>
                      {userStacksUsing.length >= 4 && (
                        <Fragment>
                          <strong>{userStacksUsingCount}</strong> developers on StackShare have
                          stated that they use <strong>{tool.name}</strong>.
                        </Fragment>
                      )}
                    </DescriptionCard>
                    <Container>
                      <ItemList
                        notFoundItem="developer stacks"
                        item={developerData}
                        hasNextPage={currentUser && hasNextPage}
                        loading={loading}
                        loadMore={onDeveloperLoadMoreClick}
                      />
                      <Center>
                        {!currentUser && hasNextPage && (
                          <SignUp text="Sign up to see more" section={USER_STACKS_USING} />
                        )}
                      </Center>
                    </Container>
                  </Fragment>
                )}
              </Fragment>
            );
          }
        }}
      </Query>
    </ComponentContainer>
  );
};

CompaniesUsingDescription.propTypes = {
  toolName: PropTypes.string,
  firstThreeCompanies: PropTypes.array,
  companyStacksUsingCount: PropTypes.number
};

Stacks.propTypes = {
  tool: PropTypes.object,
  currentUser: PropTypes.object
};

export default compose(
  withErrorBoundary(true),
  withAnalyticsPayload()
)(Stacks);
